import type { CheckoutGolfService } from '@/scopes/checkout/domain/types'
import { GOLF_NEW_SLOT_KEY } from '@/scopes/checkout/domain/types'
import { produce } from 'immer'

function buildNewSlotEditingValues(service: CheckoutGolfService) {
  const firstOption = Object.values(service.options)[0]
  return {
    locationId: firstOption?.locationId ?? '',
    date: firstOption?.date ?? '',
    time: firstOption?.time ?? '',
    holes: firstOption ? Number(firstOption.holes) : 0,
    withBuggy: firstOption ? firstOption.withBuggy : false,
    participantIds: [],
    optionId: firstOption?.id ?? '',
  }
}

/** Updates the location filter for a golf editing slot and resets dependent fields */
export function updateEditingGolfSlotLocation(payload: { slotId: string | null, locationId: string }) {
  return produce((service: CheckoutGolfService) => {
    if (!payload.slotId)
      return
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return

    slot.locationId = payload.locationId

    const matchingOptionId = Object.keys(service.options).find(id =>
      service.options[id].locationId === payload.locationId
      && service.options[id].holes === slot.holes
      && service.options[id].withBuggy === slot.withBuggy
      && service.options[id].date === slot.date,
    )

    if (matchingOptionId) {
      slot.optionId = matchingOptionId
      return
    }

    const firstOptionId = Object.keys(service.options).find(id =>
      service.options[id].locationId === payload.locationId,
    )

    if (firstOptionId) {
      const firstOption = service.options[firstOptionId]
      slot.optionId = firstOptionId
      slot.holes = firstOption.holes
      slot.withBuggy = firstOption.withBuggy
      slot.date = firstOption.date
    }
    else {
      slot.optionId = ''
    }
  })
}

/** Updates the holes filter for a golf editing slot and resets the selected option */
export function updateEditingGolfSlotHoles(payload: { slotId: string, holes: number }) {
  return produce((service: CheckoutGolfService) => {
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return
    const optionId = Object.keys(service.options).find(id =>
      service.options[id].locationId === slot.locationId
      && service.options[id].holes === payload.holes
      && service.options[id].withBuggy === slot.withBuggy
      && service.options[id].date === slot.date,
    )
    slot.holes = payload.holes
    slot.optionId = optionId ?? ''
  })
}

/** Updates the date filter for a golf editing slot and resets the selected option */
export function updateEditingGolfSlotDate(payload: { slotId: string | null, date: string }) {
  return produce((service: CheckoutGolfService) => {
    if (!payload.slotId)
      return
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return
    slot.date = payload.date

    const optionId = Object.keys(service.options).find(id =>
      service.options[id].locationId === slot.locationId
      && service.options[id].holes === slot.holes
      && service.options[id].withBuggy === slot.withBuggy
      && service.options[id].date === payload.date,
    )

    slot.optionId = optionId ?? ''
  })
}

/** Updates the buggy toggle for a golf editing slot and resets the selected option */
export function updateEditingGolfSlotBuggy(payload: { slotId: string, withBuggy: boolean }) {
  return produce((service: CheckoutGolfService) => {
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return
    slot.withBuggy = payload.withBuggy

    const optionId = Object.keys(service.options).find(id =>
      service.options[id].locationId === slot.locationId
      && service.options[id].holes === slot.holes
      && service.options[id].date === slot.date
      && service.options[id].withBuggy === payload.withBuggy,
    )

    slot.optionId = optionId ?? ''
    if (payload.withBuggy && service.locations[slot.locationId]?.isBuggyEligible) {
      slot.holes = 18
    }
  })
}

/** Updates the participant selection for a golf editing slot */
export function updateEditingGolfSlotParticipants(payload: { slotId: string, participantIds: string[] }) {
  return produce((service: CheckoutGolfService) => {
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return
    slot.participantIds = payload.participantIds
  })
}

/** Saves the editing slot into selectedValues; resets the blank new-slot entry */
export function saveGolfSlot(payload: { slotId: string }) {
  return produce((service: CheckoutGolfService) => {
    const editingSlot = service.editingSelectedValues[payload.slotId]
    if (!editingSlot || !editingSlot.optionId)
      return

    service.selectedValues[editingSlot.optionId] = { ...editingSlot }

    if (payload.slotId === GOLF_NEW_SLOT_KEY) {
      service.editingSelectedValues[GOLF_NEW_SLOT_KEY] = buildNewSlotEditingValues(service)
    }
    else if (payload.slotId !== editingSlot.optionId) {
      delete service.selectedValues[payload.slotId]
      delete service.editingSelectedValues[payload.slotId]
      service.editingSelectedValues[editingSlot.optionId] = { ...editingSlot }
    }
  })
}

/** Cancels editing and reverts the slot to its last saved state */
export function cancelGolfSlotEditing(payload: { slotId: string }) {
  return produce((service: CheckoutGolfService) => {
    const saved = service.selectedValues[payload.slotId]
    if (saved) {
      service.editingSelectedValues[payload.slotId] = { ...saved }
    }
    else if (payload.slotId === GOLF_NEW_SLOT_KEY) {
      service.editingSelectedValues[GOLF_NEW_SLOT_KEY] = buildNewSlotEditingValues(service)
    }
  })
}

/** Removes a saved golf slot entirely */
export function removeGolfSlot(payload: { slotId: string }) {
  return produce((service: CheckoutGolfService) => {
    delete service.selectedValues[payload.slotId]
    delete service.editingSelectedValues[payload.slotId]
  })
}

export function updateEditingGolfSlotTime(payload: { slotId: string, time: string }) {
  return produce((service: CheckoutGolfService) => {
    const slot = service.editingSelectedValues[payload.slotId]
    if (!slot)
      return
    slot.time = payload.time
  })
}
